import { useState } from "react";
import { CircleCheckIcon, CirclePlusIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useCreateAddCandidacyEvaluationNew } from "@/hooks/createAdds/useCreateAddCandidacyEvaluationNew";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useGetCandidacyEvaluationNew } from "@/hooks/queries/useGetCandidacyEvaluationNew";
import { CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME } from "@/lib/constants";
import { cn } from "@/lib/utils";

export const AddLogisticsToEvaluationControl = ({
  candidacyId,
  subtitle,
  title,
}: {
  candidacyId: string;
  subtitle: string;
  title: string;
}) => {
  /* Context */
  const { isReadOnly } = useUserSheet();

  /* Database CRUD Operations */
  // Read
  const evaluationQuery = useGetCandidacyEvaluationNew({ candidacyId });
  // Create
  const { isPending, mutate: createEvaluation } =
    useCreateAddCandidacyEvaluationNew();

  /* React Hooks */
  const [isOpen, setIsOpen] = useState(false);
  const [note, setNote] = useState("");

  /* Helpers */
  const evaluation = evaluationQuery.data;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- legacy records may omit followUps
  const followUps = evaluation?.followUps || [];
  const isAdded = followUps.some(
    (followUp) => followUp.title === title && followUp.subtitle === subtitle,
  );

  /* Handlers */
  const handleAdd = () => {
    const currentEvaluation = {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- legacy records may omit evaluation
      evaluation: evaluation?.evaluation || "",
      followUps,
    };

    createEvaluation(
      {
        candidacyId,
        evaluation: {
          followUps: [
            ...currentEvaluation.followUps,
            {
              note: note.trim(),
              subtitle,
              title,
            },
          ],
          evaluation: currentEvaluation.evaluation,
        },
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          setNote("");
        },
      },
    );
  };

  /* Render */
  const render = () => {
    if (isReadOnly) return null;
    if (isAdded) {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-7 w-7 shrink-0 rounded-full p-0 text-primary-700 disabled:opacity-100"
                disabled
                size="icon"
                type="button"
                variant="ghost"
              >
                <CircleCheckIcon className="h-[18px] w-[18px]" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Added to Evaluation</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <Popover onOpenChange={setIsOpen} open={isOpen}>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  className={cn(
                    "h-7 w-7 shrink-0 rounded-full bg-slate-100 p-0 text-primary-900 opacity-0 transition-opacity duration-150 hover:bg-slate-200 hover:text-primary-900 group-hover/evaluation:opacity-100",
                    isOpen && "opacity-100",
                  )}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <CirclePlusIcon className="h-[18px] w-[18px]" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>Add to Evaluation</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <PopoverContent align="end" className="w-80 p-4">
          <Column className="gap-3">
            <Column className="gap-1">
              <Text className="text-sm font-medium text-slate-800">
                Add to Evaluation
              </Text>
              <Text className="text-xs font-normal text-slate-500">
                {title}
              </Text>
            </Column>
            <Column className="gap-1.5">
              <Text className={CANDIDATE_SHEET_KICKER_LABEL_CLASSNAME}>
                Note · optional
              </Text>
              <Textarea
                className="min-h-24 resize-none"
                onChange={(event) => {
                  setNote(event.target.value);
                }}
                placeholder="Add context for your evaluation..."
                value={note}
              />
            </Column>
            <Row className="justify-end gap-2">
              <Button
                onClick={() => {
                  setIsOpen(false);
                }}
                size="sm"
                type="button"
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                disabled={isPending}
                onClick={handleAdd}
                size="sm"
                type="button"
              >
                Add
              </Button>
            </Row>
          </Column>
        </PopoverContent>
      </Popover>
    );
  };

  return render();
};
